import { addDays, startOfDay } from "date-fns"

export const SHOPS = [
  "Avionics",
  "Structures",
  "Interiors",
  "Powerplant",
  "Gulfstream",
  "Bombardier",
  "Embraer",
  "Pilatus",
  "King Air",
  "Falcon",
] as const

export type Shop = (typeof SHOPS)[number]

export type WorkOrder = {
  id: string
  tail: string
  aircraftType: string
  title: string
  start: number
  end: number
  need: Partial<Record<Shop, number>>
  quoted: boolean
}

export type Person = {
  id: string
  name: string
  role: string
  shop: Shop
  line: boolean
  off: ReadonlySet<number>
  training: ReadonlySet<number>
}

export const TODAY = startOfDay(new Date())

export const FIRST_DAY = -400

export const LAST_DAY = 400

export function dateOf(day: number): Date {
  return addDays(TODAY, day)
}

export function isWeekend(day: number): boolean {
  const weekday = dateOf(day).getDay()
  return weekday === 0 || weekday === 6
}

function workOrder(
  id: string,
  tail: string,
  aircraftType: string,
  title: string,
  start: number,
  end: number,
  need: Partial<Record<Shop, number>>,
  quoted = false,
): WorkOrder {
  return { id, tail, aircraftType, title, start, end, need, quoted }
}

export const WORK_ORDERS: readonly WorkOrder[] = [
  workOrder("WO-1188", "N222JC", "G450", "2A check", -7, 12, {
    Gulfstream: 4,
    Avionics: 2,
  }),
  workOrder("WO-1194", "N620DX", "Challenger 350", "600 FH inspection", -3, 8, {
    Interiors: 6,
    Avionics: 3,
    Structures: 2,
  }),
  workOrder("WO-1197", "N852AL", "Falcon 2000", "C check", 4, 29, {
    Powerplant: 2,
    Avionics: 4,
    Structures: 2,
    Interiors: 4,
    Falcon: 4,
  }),
  workOrder("WO-1208", "N877FX", "Falcon 2000", "B check", -12, 2, {
    Falcon: 3,
    Avionics: 1,
  }),
  workOrder("WO-1210", "N521DC", "PC-12 NGX", "300 FH inspection", -5, 1, {
    Pilatus: 3,
  }),
  workOrder("WO-1212", "N789PC", "PC-24", "600 FH inspection", 6, 19, {
    Powerplant: 1,
    Pilatus: 4,
    Avionics: 1,
  }),
  workOrder(
    "WO-1214",
    "N654NB",
    "King Air 350",
    "Phase 3 + AD 2026-14-07",
    -3,
    9,
    { "King Air": 3 },
  ),
  workOrder("WO-1215", "N418KP", "King Air 350", "Phase 2", 14, 20, {
    "King Air": 4,
  }),
  workOrder(
    "WO-1216",
    "N990AA",
    "Challenger 350",
    "AOG · No. 2 hydraulic pump",
    -2,
    3,
    { Bombardier: 4 },
  ),
  workOrder(
    "WO-1217",
    "N3062P",
    "Phenom 300",
    "AOG · LH leading-edge inspection",
    0,
    1,
    { Embraer: 3 },
  ),
  workOrder(
    "WO-1218",
    "N566GL",
    "Global 6000",
    "Field service · brake change",
    8,
    10,
    { Bombardier: 2 },
  ),
  workOrder(
    "Q-2314",
    "N701RH",
    "PC-24",
    "12 mo inspection",
    22,
    30,
    { Pilatus: 3, Avionics: 1 },
    true,
  ),
  workOrder(
    "Q-2316",
    "N929T",
    "Falcon 2000",
    "Doc 8 · landing gear",
    30,
    45,
    { Falcon: 5, Structures: 2 },
    true,
  ),
  workOrder("WO-1183", "N585GS", "G450", "Cabin refurbishment", -10, 1, {
    Interiors: 5,
    Avionics: 2,
    Gulfstream: 2,
  }),
  workOrder("WO-1199", "N278GS", "PC-24", "300 FH / 12 mo inspection", -1, 5, {
    Structures: 1,
    Interiors: 2,
    Avionics: 1,
  }),
  workOrder("WO-1202", "N641AT", "King Air 350", "Avionics retrofit", 4, 8, {
    Avionics: 3,
  }),
  workOrder("WO-1190", "N127GG", "Global 6000", "750 FH inspection", -3, 8, {
    Gulfstream: 3,
    Structures: 2,
  }),
  workOrder("WO-1196", "N1220A", "Citation XLS", "Doc 6 · 600 FH", -3, 15, {
    Structures: 3,
  }),
  workOrder("WO-1205", "N40N", "PC-12 NGX", "Interior soft goods", -3, 22, {
    Interiors: 3,
  }),
  workOrder(
    "Q-2311",
    "N410GS",
    "Phenom 300",
    "1200 FH inspection",
    11,
    22,
    { Structures: 3, Interiors: 4, Gulfstream: 2 },
    true,
  ),
  workOrder(
    "Q-2308",
    "N11CP",
    "Citation CJ3",
    "Doc 10 inspection",
    4,
    15,
    { Structures: 2, Interiors: 2 },
    true,
  ),
]

export const WORK_ORDER_BY_ID: ReadonlyMap<string, WorkOrder> = new Map(
  WORK_ORDERS.map((order) => [order.id, order]),
)

const ROSTER: Record<Shop, string[]> = {
  Avionics: [
    "Dana Whitlock|Team lead",
    "Arlo Penhale",
    "Corin Maddox",
    "Teodor Valsh",
    "Priya Ancell|Line",
    "Rhys Calloway|Line",
    "Juno Marrick",
    "Silas Brenner|Line",
    "Wren Oakes",
  ],
  Structures: [
    "Hollis Varnum",
    "Mateo Quill",
    "Bryn Halvorsen|Team lead",
    "Kasimir Doyle",
    "Odette Ferris|On-call",
    "Linus Grayford",
    "Tamsin Rowe",
  ],
  Interiors: [
    "Ines Calder",
    "Rowan Pike",
    "Felix Amberly",
    "Noor Haddad",
    "Callum Trent",
    "Esme Lockhart",
    "Dorian Vale",
    "Mirela Stoyan",
    "Ezra Kingsley",
    "Talia Brook",
    "Soren Ashdown",
    "Vera Moulding",
    "Gideon Fairweather",
    "Lena Hartigan",
    "Otis Whelan",
    "Yara Penrose",
  ],
  Powerplant: [
    "Magnus Ellery|Team lead",
    "Cyrus Bellamy",
    "Hana Stroud",
    "Idris Fenwick",
    "Clara Wynn",
  ],
  Gulfstream: [
    "Jasper Holloway",
    "Marisol Teague",
    "Desmond Achebe",
    "Freya Lindqvist|Team lead",
    "Anselm Ruiz",
    "Pippa Garland",
  ],
  Bombardier: [
    "Tobias Wren|Team lead",
    "Ottilie Marsh|On-call",
    "Kiran Bhatt",
    "Emeric Szabo",
    "Lyra Castellan",
    "Bastian Morrow",
  ],
  Embraer: [
    "Raquel Antunes|Team lead",
    "Dmitri Volkonsky",
    "Saskia Brandt",
    "Oren Galloway",
    "Petra Novak",
  ],
  Pilatus: [
    "Anders Kjell|Team lead",
    "Quentin Hale",
    "Maeve Corrigan",
    "Lior Ashkenazi",
    "Ruben Okonkwo",
  ],
  "King Air": [
    "Celeste Vaughan|Team lead",
    "Harlan Price",
    "Imogen Sato",
    "Wesley Thorne",
  ],
  Falcon: [
    "Gaspard Lemaire|Team lead",
    "Nadia Orlov",
    "Elio Bianchi",
    "Aurelie Dumont",
    "Fintan Byrne",
  ],
}

export const TRAINING_COURSE: Record<Shop, string> = {
  Avionics: "Garmin G5000 recurrent",
  Structures: "Composite repair level 2",
  Interiors: "Cabin systems refresher",
  Powerplant: "PW300 borescope recurrent",
  Gulfstream: "G450 maintenance initial",
  Bombardier: "Challenger 350 systems refresher",
  Embraer: "Phenom 300 line & base",
  Pilatus: "PC-24 line & base",
  "King Air": "PT6A hot section",
  Falcon: "Falcon 2000 EASy II",
}

function seededRandom(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function daySpan(from: number, length: number): Set<number> {
  return new Set(Array.from({ length }, (_, offset) => from + offset))
}

function buildCrew(): Record<Shop, Person[]> {
  const random = seededRandom(7)
  const crew = (shop: Shop, shopIndex: number): Person[] =>
    ROSTER[shop].map((entry, index) => {
      const [name = entry, role = ""] = entry.split("|")
      const line = role === "Line"
      const off =
        random() < 0.4
          ? daySpan(
              -21 + Math.floor(random() * 70),
              2 + Math.floor(random() * 4),
            )
          : new Set<number>()
      const training =
        index === 2 && !line
          ? daySpan(-3 + shopIndex * 7, 5)
          : new Set<number>()
      return { id: `${shop}-${index}`, name, role, shop, line, off, training }
    })
  return {
    Avionics: crew("Avionics", 0),
    Structures: crew("Structures", 1),
    Interiors: crew("Interiors", 2),
    Powerplant: crew("Powerplant", 3),
    Gulfstream: crew("Gulfstream", 4),
    Bombardier: crew("Bombardier", 5),
    Embraer: crew("Embraer", 6),
    Pilatus: crew("Pilatus", 7),
    "King Air": crew("King Air", 8),
    Falcon: crew("Falcon", 9),
  }
}

export const CREW: Record<Shop, Person[]> = buildCrew()

export const PEOPLE: readonly Person[] = SHOPS.flatMap((shop) => CREW[shop])
